import { 
  FileInput, 
  Cpu, 
  Monitor,
  Package,
  ClipboardList,
  Users,
  Clock,
  TrendingUp,
  Gauge,
  Target,
  AlertTriangle,
  Activity,
  GitBranch
} from "lucide-react";

export default function HowWorksSection() {
  const inputs = [
    { icon: Package, text: "Produção diária" },
    { icon: ClipboardList, text: "Pedidos recebidos" },
    { icon: TrendingUp, text: "Backlog" },
    { icon: Users, text: "Funcionários" },
    { icon: Clock, text: "Horas trabalhadas" },
  ];

  const analyses = [
    "Tendência e aceleração do backlog",
    "Intensidade estrutural",
    "Regime predominante (pressão, equilíbrio ou ociosidade)",
    "Sustentabilidade real da meta",
    "Risco de ruptura",
    "Zona de inércia operacional",
    "Desalinhamento estrutural silencioso",
  ];

  return (
    <section
      id="how-works"
      className="relative py-16 md:py-32 bg-[#0F1115]"
      data-testid="how-works-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 text-xs font-mono tracking-widest uppercase text-[#F59E0B] bg-[#0E3A46] border border-[#F59E0B]/20 rounded-sm mb-6">
            Simplicidade com Profundidade
          </span>

          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#E5E7EB]">
            Como <span className="text-[#F59E0B]">Funciona</span>
          </h2>
        </div>

        {/* Three Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Step 1 - Input */}
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8" data-testid="step-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F59E0B] text-black font-bold text-sm rounded-sm">
                1
              </div>
              <FileInput className="h-6 w-6 text-[#F59E0B]" />
              <h3 className="font-heading text-xl font-bold text-[#E5E7EB]">
                Você insere
              </h3>
            </div>

            <p className="text-sm text-[#6B7280] mb-6 font-mono tracking-wider uppercase">
              Dados simples
            </p>

            <div className="space-y-3">
              {inputs.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <item.icon className="h-4 w-4 text-[#F59E0B]" />
                  <span className="text-[#9CA3AF]">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-[#2A2F38]">
              <p className="text-sm text-[#6B7280]">
                Nada além do que você já acompanha.
              </p>
              <p className="text-xs text-[#9CA3AF] mt-2 italic">
                Você já acompanha esses números. O KpAction apenas revela o que eles significam.
              </p>
            </div>
          </div>

          {/* Step 2 - Analysis */}
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8" data-testid="step-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F59E0B] text-black font-bold text-sm rounded-sm">
                2
              </div>
              <Cpu className="h-6 w-6 text-[#F59E0B]" />
              <h3 className="font-heading text-xl font-bold text-[#E5E7EB]">
                KpAction analisa
              </h3>
            </div>

            <p className="text-sm text-[#6B7280] mb-6 font-mono tracking-wider uppercase">
              Automaticamente
            </p>

            <div className="space-y-3">
              {analyses.map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-[#9CA3AF]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3 - Output */}
          <div className="bg-[#1C1F26] border border-[#F59E0B]/30 rounded-sm p-8" data-testid="step-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#F59E0B] text-black font-bold text-sm rounded-sm">
                3
              </div>
              <Monitor className="h-6 w-6 text-[#F59E0B]" />
              <h3 className="font-heading text-xl font-bold text-[#E5E7EB]">
                Você recebe
              </h3>
            </div>

            <p className="text-sm text-[#F59E0B] mb-6 font-mono tracking-wider uppercase">
              Clareza objetiva
            </p>

            <div className="space-y-4 text-[#E5E7EB]">
              <p className="flex items-center gap-2">
                <span className="text-[#10B981]">✓</span>
                Sem interpretação emocional
              </p>
              <p className="flex items-center gap-2">
                <span className="text-[#10B981]">✓</span>
                Sem ruído
              </p>
              <p className="flex items-center gap-2">
                <span className="text-[#10B981]">✓</span>
                Sem subjetividade
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-[#2A2F38]">
              <p className="text-[#F59E0B] font-medium">
                Você continua decidindo — agora com clareza sobre o que está acontecendo na sua operação.
              </p>
            </div>
          </div>
        </div>

        {/* System Value Block */}
        <div className="max-w-3xl mx-auto mt-8">
          <div className="bg-[#1C1F26] border border-[#2A2F38] rounded-sm p-8 md:p-10 text-center">
            <p className="text-base md:text-lg text-[#E5E7EB] font-medium mb-6">
              Essa leitura é gerada por um sistema que analisa continuamente a dinâmica da sua operação:
            </p>
            <div className="text-left max-w-xl mx-auto space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">Como o backlog está evoluindo — e em que ritmo</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">Se a pressão interna está aumentando ou estabilizando</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">Quando a capacidade deixa de sustentar a demanda</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm md:text-base text-[#9CA3AF]">Se a meta definida é realmente atingível nas condições atuais</span>
              </div>
            </div>
            <div className="border-t border-[#2A2F38] pt-6 mt-4">
              <p className="text-[#9CA3AF] text-sm md:text-base mb-4">
                Você não precisa interpretar gráficos.
              </p>
              <p className="text-[#F59E0B] text-sm md:text-base font-medium">
                O sistema já traduz esses sinais em uma leitura clara do que está acontecendo — e do que tende a acontecer.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
